import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, CheckCircle, ChevronLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = ["Service", "Date & Time", "Your Details", "Review"];

export default function BookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Capture attribution source from nurture email CTA links (?source=nurture_email_2 etc.)
  const bookingSource = searchParams.get("source") || "direct";

  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [intake, setIntake] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lightAck, setLightAck] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/services`)
      .then(r => r.json())
      .then(data => {
        setServices(data);
        const preselect = searchParams.get("service");
        if (preselect) {
          const match = data.find(s => s.id === preselect);
          if (match) {
            setSelectedService(match);
            setStep(2);
          }
        }
      })
      .catch(() => {});
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      setClientName(user.name || "");
      setClientEmail(user.email || "");
    }
  }, [user]);

  const fetchSlots = useCallback(async (date) => {
    if (!date) return;
    const iso = date.toISOString().split("T")[0];
    setSlotsLoading(true);
    setSelectedSlot(null);
    try {
      const res = await fetch(`${API}/api/availability/${iso}`);
      const data = await res.json();
      setAvailableSlots(data.slots || []);
    } catch {
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  // Intentional: runs once on mount to pre-load available services; adding `fetchSlots` to deps causes re-fetch on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedDate) fetchSlots(selectedDate);
  }, [selectedDate, fetchSlots]);

  const isPastOrWeekend = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  };

  const formatDate = (d) => {
    if (!d) return "";
    return d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  const handleBook = async () => {
    if (!clientName.trim() || !clientEmail.trim()) {
      setError("Please fill in your name and email.");
      return;
    }

    // FREE service — direct booking with lightweight acknowledgment
    if (selectedService.price === 0) {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API}/api/bookings`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            service_id: selectedService.id,
            date: selectedDate.toISOString().split("T")[0],
            time_slot: selectedSlot,
            client_name: clientName.trim(),
            client_email: clientEmail.trim(),
            intake_situation: intake.trim() || null,
            origin_url: window.location.origin,
            light_acknowledgment: true,
            source: bookingSource,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Booking failed");
        navigate(
          `/booking/success?free=true&service_name=${encodeURIComponent(selectedService.name)}&date=${encodeURIComponent(formatDate(selectedDate))}&time=${encodeURIComponent(selectedSlot)}&name=${encodeURIComponent(clientName)}`,
          { replace: true }
        );
      } catch (e) {
        setError(e.message || "Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // PAID service — navigate to agreement signing flow (pass source for booking attribution)
    navigate("/agreement", {
      state: {
        service: selectedService,
        date: selectedDate.toISOString().split("T")[0],
        timeSlot: selectedSlot,
        formattedDate: formatDate(selectedDate),
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim(),
        intake: intake.trim() || null,
        source: bookingSource,
      },
    });
  };

  const canProceed = () => {
    if (step === 1) return !!selectedService;
    if (step === 2) return !!selectedDate && !!selectedSlot;
    if (step === 3) return clientName.trim() && clientEmail.trim();
    return true;
  };

  return (
    <div className="bg-ivory min-h-screen">
      {/* Header */}
      <section className="bg-navy py-20 px-6 text-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7 }}
          className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-3">
          Advisory Sessions
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
          className="font-lora text-4xl lg:text-5xl text-ivory font-normal">
          Book Your Session
        </motion.h1>
        <div className="w-10 h-px bg-gold mx-auto mt-5"></div>
      </section>

      {/* Progress */}
      <div className="border-b border-soft-gray bg-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-center gap-6">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              {i > 0 && <div className={`w-8 h-px ${i < step ? "bg-gold" : "bg-soft-gray"}`} />}
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-lora transition-all ${
                  i + 1 < step ? "bg-gold text-white" : i + 1 === step ? "bg-navy text-ivory" : "border border-soft-gray text-navy/30"
                }`}>
                  {i + 1 < step ? <CheckCircle size={12} /> : i + 1}
                </div>
                <span className={`text-xs tracking-wide ${i + 1 === step ? "block text-navy font-medium" : "hidden sm:block text-navy/40"}`}>{s}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-4xl mx-auto px-6 py-14">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>
              <h2 className="font-lora text-2xl text-navy mb-2">Select Your Service</h2>
              <p className="text-navy/50 font-serif text-sm mb-8">Begin with the complimentary call, or choose the service that fits your situation.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((svc) => (
                  <button key={svc.id} onClick={() => setSelectedService(svc)}
                    data-testid={`select-service-${svc.id}`}
                    className={`text-left p-6 border transition-all duration-200 ${
                      selectedService?.id === svc.id
                        ? "border-navy bg-navy text-ivory"
                        : svc.price === 0
                        ? "border-gold/50 bg-[#F4F3EF] hover:border-navy"
                        : "border-soft-gray bg-white hover:border-navy"
                    }`}>
                    {svc.price === 0 && selectedService?.id !== svc.id && (
                      <p className="font-lora text-xs tracking-[0.2em] uppercase text-gold mb-1">Begin Here</p>
                    )}
                    {svc.id === "career-coaching" && selectedService?.id !== svc.id && (
                      <p className="font-lora text-xs tracking-[0.2em] uppercase text-gold mb-1">Flexible Support</p>
                    )}
                    {svc.popular && selectedService?.id !== svc.id && (
                      <p className="font-lora text-xs tracking-[0.2em] uppercase text-gold mb-1">Most Comprehensive</p>
                    )}
                    <p className="font-lora text-sm font-normal mb-1">{svc.name}</p>
                    <p className={`font-lora text-lg mb-1 ${selectedService?.id === svc.id ? "text-gold" : "text-gold"}`}>
                      {svc.price === 0 ? "Complimentary" : `$${svc.price}`}
                    </p>
                    <p className={`text-xs mb-2 ${selectedService?.id === svc.id ? "text-ivory/60" : "text-navy/40"}`}>{svc.currency} · {svc.duration}</p>
                    <p className={`text-xs font-serif leading-relaxed ${selectedService?.id === svc.id ? "text-ivory/80" : "text-navy/60"}`}>{svc.description}</p>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>
              <h2 className="font-lora text-2xl text-navy mb-2">Choose a Date & Time</h2>
              <p className="text-navy/50 font-serif text-sm mb-8">All times shown in Eastern Time (Toronto). Weekday evenings, Saturdays &amp; Sundays.</p>
              <div className="grid lg:grid-cols-2 gap-10">
                <div className="bg-white border border-soft-gray p-6">
                  <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate}
                    disabled={isPastOrWeekend}
                    classNames={{
                      day_selected: "bg-navy text-ivory hover:bg-navy hover:text-ivory focus:bg-navy focus:text-ivory",
                      day_today: "border border-gold text-navy",
                    }}
                  />
                </div>
                <div>
                  {!selectedDate ? (
                    <div className="flex items-center justify-center h-full text-navy/40 font-serif text-sm py-16">
                      Select a date to view available times
                    </div>
                  ) : slotsLoading ? (
                    <div className="text-navy/40 font-serif text-sm py-8 text-center">Loading available times...</div>
                  ) : availableSlots.length === 0 ? (
                    <div className="text-navy/50 font-serif text-sm py-8 text-center">
                      No available slots for this date.<br />Please select another day.
                    </div>
                  ) : (
                    <div>
                      <p className="font-lora text-xs tracking-[0.2em] uppercase text-navy/40 mb-4">{formatDate(selectedDate)}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {availableSlots.map((slot) => (
                          <button key={slot} onClick={() => setSelectedSlot(slot)}
                            data-testid={`slot-${slot.replace(/\s|:/g, "-")}`}
                            className={`py-3 px-4 border text-sm font-serif transition-all ${
                              selectedSlot === slot
                                ? "bg-navy border-navy text-ivory"
                                : "bg-white border-soft-gray text-navy hover:border-navy"
                            }`}>
                            {slot}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>
              <h2 className="font-lora text-2xl text-navy mb-2">Your Details</h2>
              <p className="text-navy/50 font-serif text-sm mb-8">Used to confirm your booking and send a calendar invitation.</p>
              <div className="max-w-lg space-y-5">
                <div>
                  <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/50 mb-2">Full Name *</label>
                  <input type="text" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Your full name"
                    data-testid="input-name"
                    className="w-full border border-soft-gray bg-white px-4 py-3 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors" />
                </div>
                <div>
                  <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/50 mb-2">Email Address *</label>
                  <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="your@email.com"
                    data-testid="input-email"
                    className="w-full border border-soft-gray bg-white px-4 py-3 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors" />
                </div>
                <div>
                  <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/50 mb-2">What brings you in? <span className="normal-case text-navy/30">(Optional)</span></label>
                  <textarea value={intake} onChange={e => setIntake(e.target.value)} rows={4} placeholder="A brief description of your current situation or what you'd like to address..."
                    data-testid="input-intake"
                    className="w-full border border-soft-gray bg-white px-4 py-3 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors resize-none" />
                </div>
              </div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>
              <h2 className="font-lora text-2xl text-navy mb-2">Confirm Your Booking</h2>
              <p className="text-navy/50 font-serif text-sm mb-8">Please review your session details before proceeding.</p>
              <div className="max-w-lg">
                <div className="bg-white border border-soft-gray border-t-[3px] border-t-gold p-8 mb-6">
                  <p className="font-lora text-xs tracking-[0.25em] uppercase text-navy/40 mb-5">Session Details</p>
                  {[
                    ["Service", selectedService?.name],
                    ["Date", formatDate(selectedDate)],
                    ["Time", `${selectedSlot} (Eastern Time)`],
                    ["Total", selectedService?.price === 0 ? "Complimentary" : `$${selectedService?.price} CAD`],
                    ["Name", clientName],
                    ["Email", clientEmail],
                  ].map(([label, val]) => (
                    <div key={label} className="flex py-2.5 border-b border-soft-gray last:border-0">
                      <span className="text-navy/40 text-xs font-serif w-20 shrink-0">{label}</span>
                      <span className="text-navy text-sm font-serif">{val}</span>
                    </div>
                  ))}
                </div>

                {/* Free service: lightweight acknowledgment */}
                {selectedService?.price === 0 && (
                  <label className="flex items-start gap-3 cursor-pointer group mb-6 p-5 bg-white border border-soft-gray" data-testid="light-ack-label">
                    <div className="relative mt-0.5 flex-shrink-0" onClick={() => setLightAck(v => !v)}>
                      <div className={`w-5 h-5 border-2 flex items-center justify-center transition-all ${lightAck ? "bg-navy border-navy" : "bg-white border-navy/30 group-hover:border-navy/60"}`}>
                        {lightAck && <CheckCircle size={12} className="text-ivory" />}
                      </div>
                    </div>
                    <span className="text-navy/70 text-sm font-serif leading-relaxed" data-testid="light-ack-text">
                      By booking this complimentary advisory call, you acknowledge that De Nicola Consulting Co. provides strategic advisory services only and does not guarantee interviews, employment offers, admissions outcomes, or specific career results.
                    </span>
                  </label>
                )}

                {error && <p className="text-red-600 font-serif text-sm mb-4" data-testid="booking-error">{error}</p>}

                <button
                  onClick={handleBook}
                  disabled={loading || (selectedService?.price === 0 && !lightAck)}
                  data-testid="confirm-booking-btn"
                  className="w-full flex items-center justify-center gap-3 bg-navy text-ivory py-4 font-lora text-xs tracking-[0.2em] uppercase hover:bg-navy/90 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {selectedService?.price === 0 ? "Confirm Free Booking" : "Review Agreement & Continue"}
                  {!loading && <ArrowRight size={14} />}
                </button>

                <p className="text-navy/30 text-xs font-serif text-center mt-3">
                  {selectedService?.price === 0
                    ? "No payment required. You will receive a calendar invitation."
                    : "You will be asked to sign a client agreement before payment."}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-10 pt-8 border-t border-soft-gray">
          <button onClick={() => { setStep(s => s - 1); setError(""); }}
            className={`flex items-center gap-2 text-navy/50 hover:text-navy font-serif text-sm transition-colors ${step === 1 ? "invisible" : ""}`}>
            <ChevronLeft size={16} /> Back
          </button>
          {step < 4 && (
            <button onClick={() => { setStep(s => s + 1); setError(""); }}
              disabled={!canProceed()} data-testid={`step-${step}-continue`}
              className="flex items-center gap-2 bg-navy text-ivory px-8 py-3 font-lora text-xs tracking-[0.2em] uppercase hover:bg-navy/90 transition-all disabled:opacity-30 disabled:cursor-not-allowed">
              Continue <ArrowRight size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
